// SDRGuide.js — Coursera-branded, print-ready ABM x SDR guide
// Self-contained. Drop next to ABMGuide.js and route to it from App.js or your hub.

import React, { useEffect } from "react";

/* ========== Shared PDF styles (matches Marketing) ========== */
function StylesPDF() {
  return (
    <style>{`
@import url('https://fonts.googleapis.com/css2?family=Source+Sans+Pro:wght@400;600;700&display=swap');

:root{
  --c-blue:#2A73CC; --c-navy:#0E2B57; --ink:#0B1A3C; --ink70:#0B1A3CB3;
  --muted:#6B7280; --line:#E6EDF2; --bg:#F4F6FA; --card:#FFFFFF;
  --r-lg:16px; --r-md:12px; --r-sm:8px;
  --sh-sm:0 2px 4px rgba(0,0,0,.08); --sh-md:0 6px 20px rgba(0,0,0,.12);
}

*{box-sizing:border-box}
body{margin:0; background:var(--bg); color:var(--ink);
  font-family:"Source Sans Pro", system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif;}

.pdf-top{
  position:sticky; top:0; z-index:40; display:flex; align-items:center; justify-content:space-between;
  padding:12px 18px; background:#fff; border-bottom:1px solid var(--line);
}
.brand{display:flex; align-items:center; gap:8px; color:var(--ink); font-weight:700; text-decoration:none}
.forbiz{color:var(--ink70); font-weight:600}
.top-actions{display:flex; gap:8px}
.btn{display:inline-flex; align-items:center; justify-content:center; gap:8px; padding:10px 14px; border-radius:8px; font-weight:700; border:1px solid transparent}
.btn--primary{background:var(--c-blue); color:#fff}
.btn--primary:hover{filter:brightness(.97)}
.btn--ghost{background:transparent; color:var(--ink)}

.sheet{width:min(1056px, 92vw); margin:18px auto}
.page{position:relative; background:#fff; border-radius:16px; box-shadow:var(--sh-md); padding:24px; margin:16px 0}
.page__foot{position:absolute; bottom:16px; right:24px; color:var(--muted); font-size:12px}


.c{position:absolute; opacity:.18; filter:drop-shadow(0 2px 4px rgba(0,0,0,.1))}
.c--left{left:-110px; top:-70px; width:320px}
.c--ring{right:-90px; top:-50px; width:260px; opacity:.24}
.c--chip{right:90px; top:90px; width:100px; opacity:.9; filter:drop-shadow(0 8px 18px rgba(0,0,0,.22))}
.intro__title{margin:0 0 6px; font-size:40px; line-height:48px; font-weight:700}
.intro__subtitle{margin:0 0 12px; font-size:18px; line-height:26px; color:rgba(255,255,255,.95)}
.lede{max-width:780px; background:rgba(255,255,255,.08); border:1px solid rgba(255,255,255,.18);
  color:#EAF2FF; padding:10px 12px; border-radius:12px}

/* Navy section hero (top of each slide) */
.hero{position:relative; border-radius:16px; padding:20px 24px 22px; overflow:hidden; color:#fff; margin-bottom:14px;
  background:linear-gradient(180deg, var(--c-navy) 0%, #0b2a55 100%)}
.hero__bg{position:absolute; inset:0; opacity:.22}
.hero__wm{position:absolute; left:-110px; top:-50px; width:360px}
.hero__title{position:relative; z-index:1; margin:0; font-size:24px; line-height:30px; font-weight:700}

/* Panels & grids */
.grid{display:grid; grid-template-columns:1fr 1fr; gap:16px}
@media (max-width:900px){ .grid{grid-template-columns:1fr} }
.panel{background:#fff; border:1px solid var(--line); border-radius:12px; padding:16px}
.panel h3{margin:0 0 8px; font-size:18px; line-height:24px}
.h4{margin:0 0 6px; font-size:16px}
.tight{margin:0; padding-left:18px}
.tight li{margin:6px 0}
.note{margin-top:12px; padding:10px 12px; border:1px dashed var(--line); border-radius:12px; color:#374151; background:#FBFCFF}

/* Table */
.tbl{width:100%; border-collapse:separate; border-spacing:0; overflow:hidden; border:1px solid var(--line); border-radius:12px}
.tbl th,.tbl td{padding:10px 12px; text-align:left; border-bottom:1px solid var(--line)}
.tbl thead th{background:#F8FAFE; font-weight:700}
.tbl tbody tr:last-child td{border-bottom:none}

/* Two-column hero */
.hero2 { position: relative; border-radius:16px; padding:24px; overflow:hidden;
  color:#fff; background: linear-gradient(180deg, #2A73CC 0%, #1f62b2 100%); }
.hero2__grid { display:grid; grid-template-columns: 1.25fr 1fr; gap:24px; align-items:center; }
@media (max-width: 900px){ .hero2__grid { grid-template-columns:1fr; } }

.hero2__left .intro__title{ margin:0 0 6px; font-size:40px; line-height:48px; font-weight:800; }
.hero2__left .intro__subtitle{ margin:0 0 12px; font-size:18px; line-height:26px; opacity:.95; }
.hero2__left .lede{ background:rgba(255,255,255,.10); border:1px solid rgba(255,255,255,.25);
  border-radius:12px; padding:12px; color:#EAF2FF; }

/* Thumbnail “device” frame */
.device{
  background:#fff; border:1px solid #E6EDF2; border-radius:16px;
  padding:10px; box-shadow:0 10px 30px rgba(0,0,0,.12);
  width:100%; aspect-ratio: 4/3; display:flex; align-items:center; justify-content:center;
}
.device img{
  width:100%; height:100%; object-fit:cover; border-radius:12px; display:block;
}

/* Print */
@media print{
  .pdf-top{display:none !important}
  .sheet{width:auto; margin:0}
  .page{box-shadow:none; border-radius:0; margin:0; padding:20mm; page-break-after:always}
  .page__foot{bottom:10mm; right:20mm}
}
`}</style>
  );
}

/* ===== Small local components (avoid cross-file deps) ===== */
function HeroSm({ title }) {
  return (
    <div className="hero">
      <div className="hero__bg">
        <svg className="hero__wm" viewBox="0 0 200 200" aria-hidden>
          <circle cx="100" cy="100" r="100" fill="#ffffff" />
        </svg>
      </div>
      <h2 className="hero__title">{title}</h2>
    </div>
  );
}
function Note({ children }) {
  return <div className="note">{children}</div>;
}

/* ===== Assets ===== */
const LOGO_SVG =
  "https://pub-065b7688176a4969bec21707aed69fdc.r2.dev/coursera/logos/Coursera%20-%20Blue.svg";
const LOGO_PNG =
  "https://pub-065b7688176a4969bec21707aed69fdc.r2.dev/coursera/logos/Coursera%20-%20Blue.png";
const THUMB_SDR =
  "https://pub-065b7688176a4969bec21707aed69fdc.r2.dev/coursera/thumbnails/abmsdr_101.png";

/* ===== Utils ===== */
function useAutoTitle(title = "ABM + SDRs") {
  useEffect(() => {
    const prev = document.title;
    document.title = title;
    return () => (document.title = prev);
  }, [title]);
}

/* ======================== Top-level ======================== */
export default function SDRGuide() {
  useAutoTitle("ABM + SDRs");

  const pages = [
    <Slide1_Title key="p1" />,
    <Slide2_WhyDifferent key="p2" />,
    <Slide3_ABMBeforeTAL key="p3" />,
    <Slide4_BuyingCommittee key="p4" />,
    <Slide5_Rules key="p5" />,
    <Slide6_ContentSupport key="p6" />,
    <Slide7_Signals key="p7" />,
    <Slide8_Rhythm key="p8" />,
    <Slide9_KeepSimple key="p9" />,
    <Slide10_AskABM key="p10" />,
  ];

  return (
    <div className="pdf-wrap">
      <StylesPDF />

      <header className="pdf-top">
        <a className="brand" href="#sdr" aria-label="Back to SDR hub">
          <picture>
            <source srcSet={LOGO_SVG} type="image/svg+xml" />
            <img src={LOGO_PNG} alt="Coursera" height="18" />
          </picture>
          <span className="forbiz">for Business</span>
        </a>
        <div className="top-actions">
          <a className="btn btn--ghost" href="#sdr">
            Back to hub
          </a>
          <button className="btn btn--primary" onClick={() => window.print()}>
            Download PDF
          </button>
        </div>
      </header>

      <main className="sheet">
        {pages.map((node, i) => (
          <section className="page" key={i}>
            {node}
            <footer className="page__foot" aria-hidden>
              {i + 1}
            </footer>
          </section>
        ))}
      </main>
    </div>
  );
}

/* ======================== Slides ======================== */

/* Slide 1 – Title */
function Slide1_Title() {
  return (
    <div className="hero2">
      <div className="hero2__grid">
        {/* Left: text */}
        <div className="hero2__left">
          <h1 className="intro__title">
            ABM + SDRs: Working a Target Account List Together
          </h1>
          <p className="intro__subtitle">
            How Coursera’s ABM team equips SDRs to win in priority accounts
          </p>

          <div className="lede">
            This guide is for SDRs who are assigned to Coursera for Business ABM
            accounts. Your managers coach you on <b>how to sell</b>. ABM’s job
            is to give you the
            <b> who, why, and when</b>:
            <ul className="tight" style={{ marginTop: 8 }}>
              <li>Which accounts matter most</li>
              <li>Who sits on the buying committee</li>
              <li>What those buyers care about</li>
              <li>When a signal says “reach out now”</li>
            </ul>
            Goal: easier, smarter conversations in our highest-priority
            accounts—with Marketing, ABM, SDRs, and AEs moving as one team.
          </div>
        </div>

        {/* Right: thumbnail visual */}
        <div className="hero2__right">
          <div className="device" aria-hidden="true">
            <img
              src={THUMB_SDR}
              alt="ABM + SDRs cover thumbnail"
              decoding="async"
              loading="eager"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

/* Slide 2 – Why ABM Accounts Feel Different */
function Slide2_WhyDifferent() {
  return (
    <div className="doc">
      <HeroSm title="Why this TAL is not “just another list”" />
      <div className="grid">
        <section className="panel">
          <h3>What ABM accounts are</h3>
          <ul className="tight">
            <li>
              Small set of companies where Coursera can have the biggest
              impact—and leadership expects outsized results.
            </li>
            <li>Handpicked and researched by ABM, Sales, and Marketing.</li>
            <li>
              Part of a broader enterprise strategy: extraordinary buyer
              interactions, optimized rep contribution, long-term revenue
              growth.
            </li>
          </ul>
        </section>
        <section className="panel">
          <h3>When you see “ABM” or “TAL”, it means</h3>
          <ul className="tight">
            <li>We know more about this customer.</li>
            <li>We are investing more in this customer.</li>
            <li>Your time here matters more than on a random inbound.</li>
          </ul>
        </section>
      </div>
    </div>
  );
}

/* Slide 3 – What ABM Has Already Done */
function Slide3_ABMBeforeTAL() {
  return (
    <div className="doc">
      <HeroSm title="What ABM sets up before the TAL hits your desk" />
      <div className="grid">
        <section className="panel">
          <h3>Aligned on ideal customer profile</h3>
          <ul className="tight">
            <li>
              Industry, size, tech stack, talent strategy, and change signals
              that match Coursera’s strengths.
            </li>
          </ul>
          <h3 style={{ marginTop: 10 }}>
            Built a prioritized Target Account List
          </h3>
          <ul className="tight">
            <li>Tier 1: strategic “must win”</li>
            <li>Tier 2: high-potential focus</li>
            <li>Tier 3: broader coverage and nurture</li>
          </ul>
          <h3 style={{ marginTop: 10 }}>Mapped the buyer journey</h3>
          <ul className="tight">
            <li>
              From “don’t know we have a skills problem” → “selecting a partner
              and expanding.”
            </li>
          </ul>
        </section>
        <section className="panel">
          <h3>Identified audience preferences</h3>
          <ul className="tight">
            <li>Content & touch types each persona prefers at each stage.</li>
            <li>
              Where they research (reports, events, peer networks, internal
              champions).
            </li>
          </ul>
          <h3 style={{ marginTop: 10 }}>Prepared enablement assets</h3>
          <ul className="tight">
            <li>
              Buyer-relevant stories, win examples, FAQs, and talk tracks by
              stage & persona.
            </li>
          </ul>
          <Note>
            When you get a TAL, you’re not starting from scratch. ABM has done
            the homework so your time goes into high-quality outreach, not
            research and guesswork.
          </Note>
        </section>
      </div>
    </div>
  );
}

/* Slide 4 – Buying Committee */
function Slide4_BuyingCommittee() {
  return (
    <div className="doc">
      <HeroSm title="The buying committee: who you’re really calling into" />
      <div className="grid">
        <section className="panel">
          <h3>Key players</h3>
          <ul className="tight">
            <li>
              <b>L&D / Talent leaders</b> — learning impact, adoption, verified
              skills, culture of learning.
            </li>
            <li>
              <b>Tech / Data leaders</b> — platform fit, security, integrations,
              AI capabilities.
            </li>
            <li>
              <b>HR / People leaders</b> — retention, engagement, equity,
              employer brand.
            </li>
            <li>
              <b>Functional sponsors</b> — performance, productivity,
              competitive advantage.
            </li>
          </ul>
          <h3 style={{ marginTop: 10 }}>ABM supports you by</h3>
          <ul className="tight">
            <li>
              Persona one-pagers (priorities, objections, resonant proof
              points).
            </li>
            <li>
              Maps of who talks to whom when they make learning investments.
            </li>
            <li>
              Likely champions vs. likely blockers based on role and past deals.
            </li>
          </ul>
        </section>
        <section className="panel">
          <h3>How to use it</h3>
          <ul className="tight">
            <li>
              Pick first contact based on fit & influence, not just “who
              downloaded.”
            </li>
            <li>
              Adjust language to the persona:
              <ul className="tight" style={{ marginTop: 6 }}>
                <li>L&D → business outcomes & adoption</li>
                <li>Tech → platform, data, integrations, security, AI</li>
                <li>HR → people outcomes & employer brand</li>
              </ul>
            </li>
          </ul>
        </section>
      </div>
    </div>
  );
}

/* Slide 5 – Rules of Engagement */
function Slide5_Rules() {
  return (
    <div className="doc">
      <HeroSm title="SDR + AE + ABM: how we work one account as one team" />
      <div className="grid">
        <section className="panel">
          <h3>Do</h3>
          <ul className="tight">
            <li>Start from ABM persona insights instead of generic scripts.</li>
            <li>
              Use ABM stories & assets that match the buyer’s stage and role.
            </li>
            <li>
              Log touches to the shared account record so AEs/ABM see the story.
            </li>
            <li>
              Loop in your AE when new intent shows up on Tier 1 & Tier 2.
            </li>
          </ul>
          <h3 className="h4" style={{ marginTop: 10 }}>
            Don’t
          </h3>
          <ul className="tight">
            <li>
              Don’t blast every persona at once; follow ABM’s “start here.”
            </li>
            <li>
              Don’t reinvent messaging on ABM accounts—check the plays first.
            </li>
            <li>
              Don’t move stages without notes; ABM tunes programs from those
              stages.
            </li>
          </ul>
        </section>
        <section className="panel">
          <h3>ABM’s promise</h3>
          <ul className="tight">
            <li>Clear ownership, simple rules, and no extra paperwork.</li>
            <li>
              If it isn’t helping you have better conversations, we change it.
            </li>
          </ul>
        </section>
      </div>
    </div>
  );
}

/* Slide 6 – Content & Campaign Support */
function Slide6_ContentSupport() {
  return (
    <div className="doc">
      <HeroSm title="From “random content” to stage-specific fuel for your sequences" />
      <div className="grid">
        <section className="panel">
          <h3>What buyers want (what research shows)</h3>
          <ul className="tight">
            <li>Most B2B content feels biased and generic.</li>
            <li>They want providers who understand their business & role.</li>
            <li>
              They want relevant content for each stage of the buying process.
            </li>
          </ul>
          <h3 style={{ marginTop: 10 }}>ABM builds three layers for you</h3>
          <ul className="tight">
            <li>
              <b>Sales competence</b> — talk tracks aligned to our methodology
              and journey; quick primers for skills, AI, and outcomes.
            </li>
            <li>
              <b>Sales confidence</b> — curated wins, outcomes, and competitive
              FAQs.
            </li>
            <li>
              <b>Sales content</b> — pre-built email/LI snippets tied to
              stage-mapped assets.
            </li>
          </ul>
        </section>
        <section className="panel">
          <h3>How to use it</h3>
          <ul className="tight">
            <li>
              Pick the sequence template labeled for your persona + stage.
            </li>
            <li>Drop in the ABM-curated asset—no hunting through folders.</li>
            <li>Use win examples & FAQs in follow-ups and call prep.</li>
          </ul>
        </section>
      </div>
    </div>
  );
}

/* Slide 7 – Signals & Timing */
function Slide7_Signals() {
  return (
    <div className="doc">
      <HeroSm title="ABM signals: when an account is “warming up”" />
      <div className="grid">
        <section className="panel">
          <h3>What ABM monitors</h3>
          <ul className="tight">
            <li>Multiple people engaging with AI/skills reports & webinars.</li>
            <li>Views of Skill Tracks or Skills Dashboards content.</li>
            <li>Industry-specific stories/benchmarks activity.</li>
            <li>
              Spikes from priority personas; event registrations/attendance.
            </li>
          </ul>
          <h3 style={{ marginTop: 10 }}>Enablement you’ll get</h3>
          <ul className="tight">
            <li>
              Weekly hot-list by stage & signal:
              <ul className="tight" style={{ marginTop: 6 }}>
                <li>
                  <b>Awareness</b>: no signals → ABM runs air cover; you stay
                  light.
                </li>
                <li>
                  <b>Engagement</b>: first touches → we flag “warm accounts.”
                </li>
                <li>
                  <b>Consideration</b>: multiple signals, no meeting → ABM
                  suggests a play.
                </li>
              </ul>
            </li>
          </ul>
        </section>
        <section className="panel">
          <h3>What you’ll see & do</h3>
          <ul className="tight">
            <li>
              Short lists with stage, recent signals, recommended next touch &
              asset.
            </li>
            <li>
              <b>Your job</b>: use it to prioritize and personalize—not to build
              a dashboard.
            </li>
          </ul>
        </section>
      </div>
    </div>
  );
}

/* Slide 8 – Collaboration Rhythm */
function Slide8_Rhythm() {
  return (
    <div className="doc">
      <HeroSm title="Simple collaboration rhythm: SDR, AE, ABM" />
      <div className="grid">
        <section className="panel">
          <h3>Weekly 15-minute sync (ABM accounts)</h3>
          <ul className="tight">
            <li>Participants: SDR + AE; ABM joins when needed.</li>
            <li>Focus: top ABM accounts only, not the whole patch.</li>
          </ul>
          <h3 style={{ marginTop: 10 }}>
            Three questions per highlighted account
          </h3>
          <ul className="tight">
            <li>
              <b>Signals</b> — what changed (engagement, initiatives, hiring,
              exec moves)?
            </li>
            <li>
              <b>Angle</b> — which single value driver now (growth, innovation,
              cost, competitiveness, compliance, pilot)?
            </li>
            <li>
              <b>Stage & exit condition</b> — where are we and what needs to
              happen to move?
            </li>
          </ul>
        </section>
        <section className="panel">
          <h3>ABM’s role</h3>
          <ul className="tight">
            <li>Bring signal summaries and asset suggestions.</li>
            <li>
              Flag patterns across accounts (messages landing / not landing).
            </li>
            <li>Capture feedback to refine campaigns & content.</li>
          </ul>
        </section>
      </div>
    </div>
  );
}

/* Slide 9 – Keep It Simple */
function Slide9_KeepSimple() {
  return (
    <div className="doc">
      <HeroSm title="Keeping ABM support simple and usable for SDRs" />
      <div className="grid">
        <section className="panel">
          <h3>Our commitments</h3>
          <ul className="tight">
            <li>
              <b>One source of truth per account</b> — concise CRM summary: key
              personas, latest signal, angle, next step.
            </li>
            <li>
              <b>Right-sized research</b> — one credible signal is enough to
              start; no 20-page dossiers.
            </li>
            <li>
              <b>Focused messaging</b> — one main value driver per sequence.
            </li>
            <li>
              <b>Lean enablement</b> — short one-pagers, snippets, and examples
              you can use the same day.
            </li>
          </ul>
          <Note>
            If anything feels heavy or confusing, that’s ABM’s problem to fix,
            not yours.
          </Note>
        </section>
      </div>
    </div>
  );
}

/* Slide 10 – How to Ask ABM for Help */
function Slide10_AskABM() {
  return (
    <div className="doc">
      <HeroSm title="How to pull ABM in when you need support" />
      <div className="grid">
        <section className="panel">
          <h3>When to engage ABM</h3>
          <ul className="tight">
            <li>
              Unsure which persona to start with → ask for the persona summary.
            </li>
            <li>
              Replies don’t fit the messaging → send snippets so we can tune.
            </li>
            <li>
              Great win or strong convo → flag it so we can turn it into a
              reusable story.
            </li>
            <li>
              TAL doesn’t match reality → raise it; we’ll adjust tiers or swap
              accounts.
            </li>
          </ul>
        </section>
        <section className="panel">
          <h3>Closing message</h3>
          <p>
            You own the conversations; ABM owns making those conversations
            easier and sharper. Together with AEs and Marketing, we turn a small
            list of ABM accounts into outsized impact for Coursera for Business
            and for our customers.
          </p>
        </section>
      </div>
    </div>
  );
}
